import { useMemo } from 'react'

import { matchFilterString, sortArrayBy } from '@domain-group/javascript-utils'
import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

const watchEntityKeys = ['statuses']

type StatusesFilters = {
  status_keys?: string[],
  string?: string,
}

interface Options {
  entityKey?: string,
  filters?: StatusesFilters,
  performHttpRequests?: boolean,
}

function useStatuses(options: Options = {}) {
  const { entityKey, filters = {} } = options
  const { status_keys: filterStatusKeys, string: filterString } = filters

  const {
    updatedEntities: { statuses: statusesUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const statuses = useSelector(reduxState => reduxState.entities.statuses)

  const filteredStatuses = useMemo(() => {
    const filtered = Object.values(statuses).filter((status) => {
      const { key, id, title } = status

      const matchTitle: boolean = matchFilterString(title, filterString)
      const matchId: boolean = id?.toString() === filterString
      const matchString: boolean = matchId || matchTitle
      const matchStatusKeys: boolean = filterStatusKeys ? filterStatusKeys.includes(key) : true

      return matchString && matchStatusKeys
    })

    return sortArrayBy(filtered, 'asc', 'title')
  }, [statusesUpdatedAt, JSON.stringify(filters)])

  const filteredStatusesCount = filteredStatuses.length
  const hasFilteredStatuses = !!filteredStatusesCount

  const loadMorePayload = useLoadMore({
    loadedCount: filteredStatusesCount,
    filters,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingStatuses } = useReduxAction(
    'statuses',
    'loadStatuses',
    {
      entityKey,
      ...filtersWithOffset,
      limit,
    },
    [filters, performHttpRequests],
    {
      shouldPerformFn: ({ loading }) => performHttpRequests && !loading,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredStatuses,
    filteredStatusesCount,
    hasFilteredStatuses,
    loading: loadingStatuses,
  }
}

export default useStatuses
